import { ColumnDef } from '@tanstack/react-table';
import { formatInTimeZone } from 'date-fns-tz';
import { useCallback, useMemo } from 'react';
import { Info, SortingArrowDown, SortingArrowUp, maintenance } from '../../../assets/icons';
import { IconSize } from '../../../constant/IconSize.constant';
import { Charger } from '../../../stores/types';
import { formatDate, getLocale, parseDate } from '../../../utils/Date.Util';
import { CheckBox, ColorType, Icon, Label, LabelType, Switch } from '../../_ui';
import CopyTooltip from '../../_ui/copyTooltip/CopyTooltip.component';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../_ui/Tooltip.component';
import ChargerStateOfCharge from '../ChargerStateOfCharge.component';
import { ChargerStatus } from '../ChargerStatus.component';
import { getRowStatus } from '../utils';
import { getAddress } from './chargerTableUtils';
import { ChargerSortColumn, UseChargerTableColumnsProps } from './types';

export const useChargerTableColumns = ({
  t,
  isAllSelected,
  handleSelectAll,
  selectedChargers,
  handleCheckboxSelection,
  isChargerLoading,
  isLocalTimezone,
  setIsLocalTimezone,
  isMultiViewActive,
  companyMap,
  sortConfig,
  handleSort,
  canEditChargers,
}: UseChargerTableColumnsProps): ColumnDef<Charger>[] => {
  const renderSortableHeader = useCallback(
    (columnId: ChargerSortColumn, content: React.ReactNode) => {
      const isActive = sortConfig.column === columnId;
      const isAsc = sortConfig.direction === 'asc';

      return (
        <div className='relative flex items-center w-full pr-6'>
          {/* Left content (text / switch / tooltip etc.) */}
          <div className='flex items-center gap-1 flex-1 min-w-0'>{content}</div>

          {/* Sort arrows */}
          <button
            type='button'
            onClick={() => handleSort(columnId)}
            className='absolute right-0 top-1/2 -translate-y-1/2 flex flex-col'
          >
            <Icon
              src={SortingArrowUp}
              size={IconSize.SIZE_12x12}
              color={isActive && isAsc ? ColorType.GREY6 : ColorType.GREY3}
            />
            <Icon
              src={SortingArrowDown}
              size={IconSize.SIZE_12x12}
              color={isActive && !isAsc ? ColorType.GREY6 : ColorType.GREY3}
              className='-mt-1'
            />
          </button>
        </div>
      );
    },
    [sortConfig, handleSort],
  );

  return useMemo<ColumnDef<Charger>[]>(() => {
    const cols: ColumnDef<Charger>[] = [];

    if (canEditChargers) {
      cols.push({
        id: 'select',
        size: 30,
        minSize: 30,
        enableResizing: false,
        header: () => (
          <CheckBox
            selected={isAllSelected}
            onChange={handleSelectAll}
            title={t('input_title_select_all_charger')}
          />
        ),
        cell: ({ row }) => (
          <CheckBox
            selected={selectedChargers.some((c) => c.id === row.original.id)}
            onChange={(checked: boolean) => handleCheckboxSelection(row.original, checked)}
            title={t('input_title_select_charger', {
              chargerName: row.original.name,
            })}
          />
        ),
      });
    }

    cols.push(
      {
        id: 'charger',
        accessorKey: 'name',
        header: () => renderSortableHeader('charger', t('charger')),
        size: 120,
        minSize: 120,
        cell: ({ row }) => <CopyTooltip text={row.original.name || ''} />,
      },
      {
        id: 'make',
        accessorFn: (row) => `${row.vendor} / ${row.model}`,
        header: () => renderSortableHeader('make', t('charger_make')),
        size: 120,
        minSize: 120,
        cell: ({ row }) => (
          <Label
            className='whitespace-nowrap truncate'
            text={`${row.original.vendor} / ${row.original.model}`}
            type={LabelType.BODY3}
            color={ColorType.BLACK}
          />
        ),
      },
      {
        id: 'maxPower',
        accessorKey: 'maxPowerKW',
        header: () => renderSortableHeader('maxPower', t('charger_max_power')),
        size: 100,
        minSize: 120,
        cell: ({ row }) => (
          <Label
            className='whitespace-nowrap truncate'
            text={row.original.maxPowerKW ?? ''}
            type={LabelType.BODY3}
            color={ColorType.BLACK}
          />
        ),
      },
      {
        id: 'maxAmperage',
        accessorKey: 'maxAmperage',
        header: () => renderSortableHeader('maxAmperage', t('charger_max_amperage')),
        size: 100,
        minSize: 100,
        cell: ({ row }) => (
          <Label
            className='whitespace-nowrap truncate'
            text={row.original.maxAmperage ?? ''}
            type={LabelType.BODY3}
            color={ColorType.BLACK}
          />
        ),
      },
      {
        id: 'serialNumber',
        accessorKey: 'serialNumber',
        header: () => renderSortableHeader('serialNumber', t('serial_number')),
        size: 120,
        minSize: 120,
        cell: ({ row }) => (
          <Label
            className='whitespace-nowrap truncate'
            text={row.original.serialNumber || ''}
            type={LabelType.BODY3}
            color={ColorType.BLACK}
          />
        ),
      },
      {
        id: 'ocppId',
        accessorKey: 'ocppId',
        header: () => renderSortableHeader('ocppId', t('chargers_ocpp_id')),
        size: 120,
        minSize: 120,
        cell: ({ row }) => (
          <Label
            className='whitespace-nowrap truncate'
            text={row.original.ocppId || ''}
            type={LabelType.BODY3}
            color={ColorType.BLACK}
          />
        ),
      },
      {
        id: 'location',
        accessorFn: (row) => row.location?.name,
        header: () => renderSortableHeader('location', t('location')),
        size: 120,
        minSize: 120,
        cell: ({ row }) => (
          <Label
            text={row.original.location?.name || ''}
            type={LabelType.BODY3}
            className='truncate'
          />
        ),
      },
      {
        id: 'address',
        accessorFn: (row) => getAddress(row),
        header: () => renderSortableHeader('address', t('charger_address')),
        size: 220,
        minSize: 220,
        cell: ({ row }) => (
          <Label
            className='truncate'
            text={getAddress(row.original)}
            type={LabelType.BODY3}
            color={ColorType.BLACK}
          />
        ),
      },
      {
        id: 'timezone',
        accessorFn: (row) => row.location?.timeZone,
        header: () => renderSortableHeader('timezone', t('location_timezone')),
        size: 120,
        minSize: 120,
        cell: ({ row }) => (
          <Label
            className='truncate'
            text={row.original.location?.timeZone || ''}
            type={LabelType.BODY3}
            color={ColorType.BLACK}
          />
        ),
      },
      {
        id: 'status',
        accessorKey: 'status',
        header: () => renderSortableHeader('status', t('status')),
        size: 150,
        minSize: 150,
        cell: ({ row }) => {
          const { timeStamp, status: chargerStatus, ports } = getRowStatus(row.original);
          return (
            <div className='whitespace-nowrap flex'>
              <ChargerStatus
                status={chargerStatus}
                ports={ports}
                timeStamp={timeStamp}
                isLoading={isChargerLoading}
              />
              {row.original.maintenanceFlag && (
                <Tooltip>
                  <TooltipTrigger>
                    <Icon className='ml-2' src={maintenance} size={IconSize.SIZE_20x20} />
                  </TooltipTrigger>
                  <TooltipContent>
                    <span className='text-sm text-white font-medium'>
                      {t('maintenance_flag_tip')}
                    </span>
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
          );
        },
      },
      {
        id: 'stateOfCharge',
        header: () => renderSortableHeader('stateOfCharge', t('chargers_grid_stateOfCharge')),
        size: 100,
        minSize: 100,
        cell: ({ row }) => (
          <ChargerStateOfCharge ports={row.original.ports} isLoading={isChargerLoading} />
        ),
      },
      {
        id: 'lastUsed',
        size: 180,
        minSize: 180,
        accessorKey: 'lastUsed',
        header: () =>
          renderSortableHeader(
            'lastUsed',
            <>
              <span>{t('chargers_grid_lastUsed')}</span>
              <Switch
                value={isLocalTimezone}
                onChange={setIsLocalTimezone}
                labelColor={ColorType.GREY6}
                enableLabel={t('timezone_toggle_local')}
                disableLabel={t('timezone_toggle_local')}
                switchClass='w-5 h-2.5'
                switchBtnClass='w-2 h-2 top-px left-px'
                translateClass='translate-x-2.5'
                className='gap-1 ml-2'
              />
              <Tooltip>
                <TooltipTrigger>
                  <Icon
                    src={Info}
                    size={IconSize.SIZE_16x16}
                    color={ColorType.GREY6}
                    className='ml-1'
                  />
                </TooltipTrigger>
                <TooltipContent>
                  <span className='text-sm text-white font-medium'>
                    {t(isLocalTimezone ? 'local_toggle_message' : 'enable_local_toggle_message')}
                  </span>
                </TooltipContent>
              </Tooltip>
            </>,
          ),
        cell: ({ row }) => {
          const charger = row.original;
          const timezone = isLocalTimezone
            ? Intl.DateTimeFormat().resolvedOptions().timeZone
            : charger.location?.timeZone;

          return (
            <Label
              className='whitespace-nowrap'
              text={
                charger.lastUsed && timezone
                  ? formatInTimeZone(
                      charger.lastUsed,
                      timezone,
                      'LLL dd, yyyy, h:mm a',
                      getLocale(),
                    )
                  : ''
              }
              type={LabelType.BODY3}
              color={ColorType.BLACK}
            />
          );
        },
      },
      {
        id: 'pricing',
        accessorFn: (row) => row.isFree,
        header: () => renderSortableHeader('pricing', t('pricing')),
        size: 100,
        minSize: 100,
        cell: ({ row }) => (
          <Label
            text={row.original.isFree ? t('chargers_price_free') : t('chargers_price_paid')}
            type={LabelType.BODY3}
            color={ColorType.BLACK}
          />
        ),
      },
      {
        id: 'pricingRule',
        accessorKey: 'priceModelDisplayName',
        header: () => renderSortableHeader('pricingRule', t('chargers_pricing_rule')),
        size: 150,
        minSize: 150,
        cell: ({ row }) => (
          <Label
            className='whitespace-nowrap truncate'
            text={row.original.priceModelDisplayName || ''}
            type={LabelType.BODY3}
            color={ColorType.BLACK}
          />
        ),
      },
      {
        id: 'access',
        accessorKey: 'access',
        header: () => renderSortableHeader('access', t('access')),
        size: 100,
        minSize: 100,
        cell: ({ row }) => (
          <Label
            text={t((row.original.access || '').toLowerCase())}
            type={LabelType.BODY3}
            color={ColorType.BLACK}
          />
        ),
      },
      {
        id: 'mapVisibility',
        accessorKey: 'hiddenOnMap',
        header: () => renderSortableHeader('mapVisibility', t('chargers_map_visibility')),
        size: 120,
        minSize: 120,
        cell: ({ row }) => (
          <Label
            text={
              row.original.hiddenOnMap
                ? t('charger_map_visibility_false')
                : t('chargers_visibility_user_access')
            }
            type={LabelType.BODY3}
            color={ColorType.BLACK}
          />
        ),
      },
      {
        id: 'powerManaged',
        accessorKey: 'isPowerManaged',
        header: () => renderSortableHeader('powerManaged', t('charger_power_managed')),
        size: 120,
        minSize: 120,
        cell: ({ row }) => (
          <Label
            text={row.original.isPowerManaged ? t('chargers_yes') : t('chargers_no')}
            type={LabelType.BODY3}
            color={ColorType.BLACK}
          />
        ),
      },
      {
        id: 'autoStart',
        accessorKey: 'autoStartEnabled',
        header: () => renderSortableHeader('autoStart', t('charger_auto_start')),
        size: 120,
        minSize: 120,
        cell: ({ row }) => (
          <Label
            text={
              row.original.autoStartEnabled
                ? t('charger_auto_start_status_true')
                : t('charger_auto_start_status_false')
            }
            type={LabelType.BODY3}
            color={ColorType.BLACK}
          />
        ),
      },
      {
        id: 'parkingSpace',
        accessorKey: 'parkingSpot',
        header: () => renderSortableHeader('parkingSpace', t('charger_parking_space')),
        size: 120,
        minSize: 120,
        cell: ({ row }) => (
          <Label
            className='whitespace-nowrap truncate'
            text={row.original.parkingSpot || ''}
            type={LabelType.BODY3}
            color={ColorType.BLACK}
          />
        ),
      },
      {
        id: 'unit',
        accessorKey: 'associatedBuildingUnit',
        header: () => renderSortableHeader('unit', t('charger_unit')),
        size: 120,
        minSize: 120,
        cell: ({ row }) => (
          <Label
            className='whitespace-nowrap truncate'
            text={row.original.associatedBuildingUnit || ''}
            type={LabelType.BODY3}
            color={ColorType.BLACK}
          />
        ),
      },
      {
        id: 'note',
        accessorKey: 'internalNote',
        header: () => renderSortableHeader('note', t('chargers_grid_note')),
        size: 220,
        minSize: 220,
        cell: ({ row }) => (
          <div className='overflow-hidden text-ellipsis'>
            <Label
              text={row.original.internalNote || ''}
              type={LabelType.BODY3}
              color={ColorType.BLACK}
            />
          </div>
        ),
      },
      {
        id: 'addedDate',
        accessorFn: (row) => row.deploymentInfo?.addedDate,
        header: () => renderSortableHeader('addedDate', t('charger_deployment_date_added')),
        size: 180,
        minSize: 180,
        cell: ({ row }) => (
          <Label
            className='whitespace-nowrap truncate'
            text={formatDate(parseDate(row.original.deploymentInfo?.addedDate || '', 'yyyy-MM-dd'))}
            type={LabelType.BODY3}
            color={ColorType.BLACK}
          />
        ),
      },
    );

    // Add company column for multi-view
    if (isMultiViewActive && companyMap) {
      const companyColumnIndex = canEditChargers ? 3 : 2;
      cols.splice(companyColumnIndex, 0, {
        id: 'company',
        accessorKey: 'ownerId',
        header: () => renderSortableHeader('company', t('company')),
        size: 156,
        minSize: 120,
        cell: ({ row }) => {
          const company = companyMap[row.original.ownerId ?? ''];
          return (
            <Label
              className='whitespace-nowrap truncate'
              text={company?.name || ''}
              type={LabelType.BODY3}
              color={ColorType.BLACK}
            />
          );
        },
      });
    }

    return cols;
  }, [
    t,
    isAllSelected,
    handleSelectAll,
    selectedChargers,
    handleCheckboxSelection,
    isChargerLoading,
    isLocalTimezone,
    setIsLocalTimezone,
    isMultiViewActive,
    companyMap,
    canEditChargers,
    renderSortableHeader,
  ]);
};
